<template>
    <div>
        <report-header
            :title="title"
            :isShowDateRange="true"
            :formatDateRangeTitle="dateRange"
        />
        <b-card id="report-card">
            <div id="report-body" class="w-100">
                <div v-if="showDummyTable">
                    <p class="item-name">
                        Loading details..
                    </p>
                    <QTable
                        :columns="defaultLoadingColumns"
                        :data="[]"
                        hide-bottom
                        :loading="isLoading"
                        :rows-per-page-options="[0]"
                    >
                    </QTable>
                </div>
                <div v-else-if="result.length == 0" class="mt-2 mb-2">
                    <i>No records available</i>
                </div>
                <div
                    :id="'table_' + item.key"
                    v-else
                    v-for="(item, index) in result"
                    :key="index"
                >
                    <p class="item-name">
                        {{ item.table.title }}
                        <template v-if="isNotSummary(item)">
                            -
                            <small v-if="item.table.isPaymentDetails"
                                >{{ getResultText(item.data.length - 1) }}
                            </small>
                            <small v-else>{{
                                getResultText(item.data.length)
                            }}</small>
                        </template>
                    </p>
                    <QTable
                        :columns="item.table.columns"
                        :data="item.data"
                        hide-bottom
                        :loading="isLoading"
                        :virtual-scroll-item-size="rowHeight"
                        :rows-per-page-options="[0]"
                        :virtual-scroll="isNotSummary(item)"
                        virtual-scroll-slice-size="60"
                        class="sticky-table"
                    >
                        <template v-slot:header="props">
                            <q-tr :props="props">
                                <q-th
                                    v-for="col in props.cols"
                                    :key="col.name"
                                    :props="props"
                                    class="sticky-header"
                                >
                                    <strong>{{ col.label }}</strong>
                                </q-th>
                            </q-tr>
                            <q-linear-progress
                                v-show="isLoading"
                                class="sticky-loader"
                                indeterminate
                                color="dark"
                                style="top: 48px;"
                            />
                        </template>
                        <template v-slot:body-cell-date="props">
                            <q-td
                                :props="props"
                                v-if="(props.value == 'Total')"
                            >
                                <strong>{{ props.value }}</strong>
                            </q-td>
                            <q-td :props="props" v-else>
                                {{ props.value || props.row.date_applied }}
                            </q-td>
                        </template>
                        <template v-slot:body-cell-amount="props">
                            <q-td
                                :props="props"
                                v-if="(props.row.date == 'Total')"
                            >
                                <strong>{{ props.value }}</strong>
                            </q-td>
                            <q-td :props="props" v-else>
                                {{ props.value }}
                            </q-td>
                        </template>
                        <template v-slot:body-cell-waive_date="props">
                            <q-td :props="props">
                                {{ props.row.waive_at }}
                            </q-td>
                        </template>
                        <template
                            v-if="item.key == 'Summary'"
                            v-slot:body-cell-transaction_type="props"
                        >
                            <q-td style="width: 40%;" :props="props">
                                <a
                                    href="#"
                                    v-scroll-to="
                                        gotoTable(props.row.transaction_type)
                                    "
                                >
                                    {{ props.row.transaction_type }}
                                </a>
                            </q-td>
                        </template>
                        <template
                            v-if="item.key == 'paymentSummary'"
                            v-slot:body-cell-payment_type="props"
                        >
                            <q-td style="width: 40%;" :props="props">
                                <a
                                    href="#"
                                    v-scroll-to="
                                        `#table_paymentDetails_${props.pageIndex}`
                                    "
                                >
                                    {{ props.row.payment_type }}
                                </a>
                            </q-td>
                        </template>
                        <template v-slot:body-cell-disposition="props">
                            <q-td :props="props">
                                <span>{{ props.row.return_type }}</span>
                            </q-td>
                        </template>
                        <template v-slot:body-cell-reference="props">
                            <q-td
                                v-if="props.row.invoice_encoded_id"
                                :props="props"
                            >
                                <a
                                    :href="
                                        props.row.invoice_encoded_id
                                            ? '../apps/billing/invoice/view/' +
                                              props.row.invoice_encoded_id
                                            : ''
                                    "
                                >
                                    {{ props.row.invoice }}
                                </a>
                            </q-td>
                            <q-td
                                v-else-if="props.row.reference_encoded_id"
                                :props="props"
                            >
                                <span
                                    style="display: block;"
                                    v-for="(item,
                                    itemIndex) in generateProperInvoiceData(
                                        props.row.reference,
                                        props.row.reference_encoded_id
                                    )"
                                    :key="itemIndex"
                                >
                                    <a
                                        v-if="item.url"
                                        :href="`../apps/billing/${
                                            item.label.includes('Return')
                                                ? 'returns'
                                                : 'invoice'
                                        }/view/${item.url}`"
                                    >
                                        {{ item.label }}
                                    </a>
                                    <span v-else>{{ item.label }}</span>
                                </span>
                            </q-td>
                            <q-td
                                v-else-if="props.row.return_encoded_id"
                                :props="props"
                            >
                                <a
                                    :href="
                                        '../apps/billing/returns/view/' +
                                        props.row.return_encoded_id
                                    "
                                >
                                    {{ props.row.reference }}
                                </a>
                            </q-td>
                            <q-td v-else :props="props">
                                <span>
                                    {{ props.value }}
                                </span>
                            </q-td>
                        </template>
                        <template v-slot:body-cell-client="props">
                            <q-td :props="props">
                                <span
                                    v-if="
                                        props.row.client.includes('(deleted)')
                                    "
                                >
                                    {{ props.row.client }}
                                </span>
                                <a
                                    v-else-if="USE_CLIENT_MODULE_VUE"
                                    :href="`/view/client/profile/${props.row.client_encoded_id}`"
                                >
                                    {{ props.row.client }}
                                </a>
                                <a
                                    v-else
                                    :href="
                                        '../apps/client/client/view/' +
                                        props.row.client_encoded_id
                                    "
                                >
                                    {{ props.row.client }}
                                </a>
                            </q-td>
                        </template>
                    </QTable>
                </div>
            </div>
        </b-card>
    </div>
</template>

<script>
import { QTable } from "quasar";
import Report from "@/mixins/report.js";
import ReportService from "@/services/report";
import CREPORT from "@/constants/report";
import "@/css/quasar-table.css";
export default {
    mixins: [Report],
    components: {
        QTable,
        ReportHeader: () => import("@/components/report/report-header")
    },
    props: {
        data: {
            type: Object,
            default() {
                return {
                    start: this.getStartDate("day"),
                    end: this.getStartDate("day")
                };
            }
        }
    },
    computed: {
        defaultLoadingColumns() {
            let columns = [
                {
                    name: "type",
                    label: "Type",
                    field: "type",
                    align: "left",
                    style: "white-space: nowrap"
                },
                {
                    name: "count",
                    label: "Count",
                    field: "count",
                    align: "left",
                    style: "white-space: nowrap"
                },
                {
                    name: "total",
                    label: "Total",
                    field: "total",
                    align: "left",
                    style: "white-space: nowrap"
                }
            ];

            return columns;
        }
    },
    data() {
        return {
            locale: "en-US",
            virtualTable: [
                "invoiceSummary",
                "return",
                "credits",
                "creditApplied",
                "refund",
                "lateFees",
                "lateFeeWaive",
                "writeoff"
            ],
            result: [],
            title: "End of Day Detail",
            dateRange: "",
            rowHeight: 44,
            showDummyTable: true,
            isLoading: false,
            isError: false,
            USE_CLIENT_MODULE_VUE: process.env.USE_CLIENT_MODULE_VUE === "true"
        };
    },
    mounted() {
        let self = this;
        // --- toggle navigation options
        self.data.filterDateOption = true;
        self.data.isShowProvider = false;
        self.data.isShowDateRange = false;
        self.data.isShowDateTimeRange = true;
        self.data.datePickerOptionDefault = "1";

        self._getSetting().then(() => {
            self.data.start = self.getStartDate("day");
            self.data.end = self.getEndDate("day");
            self.data.from = self.converToUnix(self.data.start);
            self.data.to = self.converToUnix(self.data.end);
            self.init();
        });

        self.$root.$on("report-filter-date-range:refresh", self.init);
        self.$root.$on("transactions_detail:refresh", function () {
            self.init();
        });

        window.scrollTo(0, 0);
    },
    beforeDestroy: function () {
        this.data.datePickerOptionDefault = null;
        this.data.isShowDateTimeRange = false;
        this.$root.$off("report-filter-date-range:refresh", this.init);
    },
    methods: {
        async init(params) {
            let self = this;

            self.isLoading = true;
            self.isError = false;

            if (params) {
                self.data.from = params.from;
                self.data.to = params.to;
            }

            self.dateRange = self.getDateTimeRange(
                self.data.from,
                self.data.to
            );

            await ReportService.getReport(CREPORT.TYPE.TRANSACTIONS_DETAIL, {
                from: self.data.from,
                to: self.data.to
            })
                .then(res => {
                    if (res && res.message && res.message.code === 200) {
                        let tablesArr = self.generateTableArray(res.resource);
                        self.result = tablesArr;

                        self.dateRange = self.getDateTimeRange(
                            res.resource.meta.from,
                            res.resource.meta.to
                        );
                        self.isError = false;
                        self.title = res.resource.meta.title;
                    } else {
                        throw new Error("Error: Unable to get the report.");
                    }
                })
                .catch(e => {
                    console.error(e.message);
                    self.isError = true;
                    self._message("Error: Unable to get the report", "danger");
                })
                .finally(() => {
                    self.dynamicPageTitle(self.title); // failed, title using default, success, title based on meta
                    self.isLoading = false;
                    self.showDummyTable = false;
                });
        },
        getSoapURI(data) {
            return process.env.CI_APPS + "soap/soap/view/" + data.id + "/0";
        },
        generateTableArray(data = {}) {
            let excludeKeys = ["skip_to_resource", "paymentType", "meta"];
            let result = [];
            let paymentTypeIndex = 0;

            for (let key in data) {
                if (excludeKeys.includes(key)) {
                    continue;
                }
                data[key].table.columns = [];
                for (let headerIndex in data[key].table.header) {
                    let field = data[key].table.header[headerIndex]
                        .replace(" ", "_")
                        .toLowerCase();

                    if (field == "items") {
                        field = "line_items";
                    }

                    data[key].table.columns.push({
                        name: field,
                        label: data[key].table.header[headerIndex],
                        field: field,
                        align: "left",
                        style: "white-space: nowrap",
                        classes: "align-baseline"
                    });
                }

                if (key === "paymentDetails") {
                    let paymentTableInfo = data[key].table;

                    for (let paymentType in data[key].data) {
                        let records = data[key].data[paymentType];

                        result.push({
                            key: key + "_" + paymentTypeIndex,
                            table: {
                                ...paymentTableInfo,
                                title: paymentType,
                                isPaymentDetails: true
                            },
                            data: records
                        });

                        paymentTypeIndex++;
                    }
                } else {
                    result.push({
                        key: key,
                        ...data[key]
                    });
                }
            }

            return result;
        },
        gotoTable(name) {
            let mapping = {
                "Amounts invoiced": "invoiceSummary",
                "Payments collected": "paymentSummary",
                "Returns recorded": "return",
                "Credits issued": "credits",
                "Applied credits": "creditApplied",
                "Refunds issued": "refund",
                "Write-Offs": "writeoff",
                "Late fees applied": "lateFees",
                "Late fees waived": "lateFeeWaive"
            };

            return "#table_" + mapping[name];
        },
        generateProperInvoiceData(reference = "", reference_encoded_id = "") {
            if (reference === "") {
                return [];
            }

            let result = [];
            let items = reference.split("@#");
            let urls = reference_encoded_id.split("@#");

            for (let n = 0; n < items.length; n++) {
                let label = items[n];
                let url = urls[n];

                result.push({
                    label: label,
                    url: url
                });
            }

            return result;
        },
        isNotSummary(item) {
            if (item && item.table && item.table.title) {
                let theTitle = item.table.title.toUpperCase();
                return theTitle !== "SUMMARY" && theTitle !== "PAYMENT SUMMARY";
            }

            return false;
        },
        getResultText(length) {
            return length + " " + (length > 1 ? "results" : "result");
        }
    }
};
</script>

<style scoped>
p.item-name {
    font-weight: bold;
    font-size: 1.2em;
    margin: 20px 0 0 0;
    padding: 5px 15px;
    border-bottom: 1px solid #c6c6c6;
}

.sticky-table {
    border: 1px solid #eee;
    border-radius: 0;
    margin-bottom: 40px;
}

.sticky-table .q-table th {
    background: #fff !important;
    position: sticky;
    z-index: 2;
    position: -webkit-sticky;
    top: 0;
}
</style>
